import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Space, Spin, message } from "antd";
import dayjs from "dayjs";
import { fetchSprZalGraf, fetchSprZalGrafSession, fetchSprZalGrafUsl } from "./spr-zal-api";
import { readGrafSessionClipboard, writeGrafSessionClipboard } from "./spr-zal-graf-clipboard";
import SprZalGrafCopyModal from "./spr-zal-graf-copy-modal";
import SprZalGrafFactModal from "./spr-zal-graf-fact-modal";
import SprZalGrafSessionDrawer from "./spr-zal-graf-session-drawer";
import "./spr-zal-graf.css";

const { RangePicker } = DatePicker;

/** dayjs().day(): 0 = вс … 6 = сб */
const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

const defaultPeriod = () => [dayjs().startOf("month"), dayjs().endOf("month")];

const eachDay = (beg, end) => {
  const days = [];
  let d = beg.startOf("day");
  const last = end.startOf("day");
  while (d.isBefore(last) || d.isSame(last, "day")) {
    days.push(d);
    d = d.add(1, "day");
  }
  return days;
};

const minutesOfDay = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value >= 0 && value < 1) return Math.round(value * 24 * 60);
    if (value >= 0 && value < 24 * 60) return Math.round(value);
  }
  const raw = String(value);
  const timeOnly = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (timeOnly) {
    return Number(timeOnly[1]) * 60 + Number(timeOnly[2]);
  }
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.hour() * 60 + d.minute();
};

const hmFromMinutes = (minutes) => {
  if (minutes == null) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const buildTimeSlots = (planStepMin, planTimeBeg, planTimeEnd) => {
  const step = planStepMin > 0 ? planStepMin : 15;
  let startMin = minutesOfDay(planTimeBeg);
  let endMin = minutesOfDay(planTimeEnd);
  if (startMin == null) startMin = 9 * 60;
  if (endMin == null) endMin = 18 * 60;
  if (endMin <= startMin) endMin = startMin + 60;

  const startSlot = Math.round(startMin / step);
  const endSlot = Math.round(endMin / step);
  const slots = [];
  for (let i = startSlot; i < endSlot; i += 1) {
    const minutes = i * step;
    const hour = Math.floor(minutes / 60);
    const min = minutes % 60;
    slots.push({
      index: i,
      minutes,
      isHour: min === 0,
      label: min === 0 ? `${hour}:${String(min).padStart(2, "0")}` : String(min)
    });
  }
  return { step, slots, startSlot };
};

const sessionLabel = (item) => {
  const name = item.sotrName || item.SotrName;
  if (name && String(name).trim()) return String(name).trim();
  return String(item.cntMax ?? item.CntMax ?? "");
};

const minutesToDayjs = (day, minutes) =>
  day.hour(Math.floor(minutes / 60)).minute(minutes % 60).second(0).millisecond(0);

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatHm = (value) => {
  const d = dayjs(value);
  return d.isValid() ? d.format("HH:mm") : "09:00";
};

const parseHm = (hm, day) => {
  const m = String(hm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return day.hour(9).minute(0).second(0).millisecond(0);
  return day.hour(Number(m[1])).minute(Number(m[2])).second(0).millisecond(0);
};

/** График работы кабинета — как Planner1: время слева, даты сверху. */
const SprZalGraf = ({ zal, open }) => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [sessionDrawer, setSessionDrawer] = useState(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [clipboardBusy, setClipboardBusy] = useState(false);
  const [factSession, setFactSession] = useState(null);

  const load = useCallback(() => {
    if (!zal?.idZal || !period?.[0] || !period?.[1]) return;
    setLoading(true);
    setError("");
    const beg = period[0].format("YYYY-MM-DD");
    const end = period[1].format("YYYY-MM-DD");
    fetchSprZalGraf(zal.idZal, beg, end)
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Не удалось загрузить график"))
      .finally(() => setLoading(false));
  }, [zal?.idZal, period]);

  useEffect(() => {
    if (!open) return;
    setPeriod(defaultPeriod());
    setSelectedId(null);
    setSelectedCell(null);
    setSessionDrawer(null);
    setCopyModalOpen(false);
    setFactSession(null);
  }, [open, zal?.idZal]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const { step, slots, startSlot } = useMemo(
    () => buildTimeSlots(zal?.planStepMin, zal?.planTimeBeg, zal?.planTimeEnd),
    [zal?.planStepMin, zal?.planTimeBeg, zal?.planTimeEnd]
  );

  const days = useMemo(() => {
    if (!period?.[0] || !period?.[1]) return [];
    return eachDay(period[0], period[1]);
  }, [period]);

  /** dayKey -> slotOffset -> session */
  const daySessions = useMemo(() => {
    const byDay = new Map();
    items.forEach((item) => {
      const fdate = dayjs(item.fdate ?? item.Fdate);
      if (!fdate.isValid()) return;
      const dayKey = fdate.format("YYYY-MM-DD");
      const begMin = minutesOfDay(item.timeBeg ?? item.TimeBeg);
      const endMin = minutesOfDay(item.timeEnd ?? item.TimeEnd);
      if (begMin == null || endMin == null || endMin <= begMin) return;

      const startIndex = Math.round(begMin / step);
      const endIndex = Math.round(endMin / step);
      const span = Math.max(1, endIndex - startIndex);
      const slotOffset = startIndex - startSlot;
      if (slotOffset < 0 || slotOffset >= slots.length) return;

      if (!byDay.has(dayKey)) byDay.set(dayKey, new Map());
      const dayMap = byDay.get(dayKey);
      if (dayMap.has(slotOffset)) return;
      const cntPlan = Number(item.cntPlan ?? item.CntPlan ?? 0);
      const planTotal = Number(item.planTotal ?? item.PlanTotal ?? 0);
      const planDone = Number(item.planDone ?? item.PlanDone ?? 0);
      let factTone = "none";
      if (planTotal > 0 && planDone > 0) factTone = planDone < planTotal ? "partial" : "all";
      dayMap.set(slotOffset, {
        idStr: item.idStr ?? item.IdStr,
        dayKey,
        begMin,
        endMin,
        label: sessionLabel(item),
        noUsl: (item.uslCount ?? item.UslCount ?? 0) === 0,
        cntPlan,
        factTone,
        span: Math.min(span, slots.length - slotOffset)
      });
    });
    return byDay;
  }, [items, step, startSlot, slots.length]);

  const coveredSlots = useMemo(() => {
    const covered = new Map();
    daySessions.forEach((dayMap, dayKey) => {
      const set = new Set();
      dayMap.forEach((session, offset) => {
        for (let i = 1; i < session.span; i += 1) set.add(offset + i);
      });
      covered.set(dayKey, set);
    });
    return covered;
  }, [daySessions]);

  /** idStr сеанса -> {dayKey, timeLabel, cntPlan} для открытия факта. */
  const sessionById = useMemo(() => {
    const map = new Map();
    daySessions.forEach((dayMap) => {
      dayMap.forEach((session) => {
        map.set(session.idStr, {
          idStr: session.idStr,
          fdate: session.dayKey,
          timeLabel: `${hmFromMinutes(session.begMin)}–${hmFromMinutes(session.endMin)}`,
          cntPlan: session.cntPlan
        });
      });
    });
    return map;
  }, [daySessions]);

  const openAdd = useCallback(
    (defaults) => {
      if (!zal?.idZal) return;
      let resolved = defaults;
      if (!resolved && selectedCell) {
        resolved = {
          fdate: selectedCell.fdate,
          timeBeg: minutesToDayjs(selectedCell.fdate, selectedCell.minutes),
          timeEnd: minutesToDayjs(selectedCell.fdate, selectedCell.minutes + step)
        };
      }
      setSessionDrawer({
        isNew: true,
        idStr: null,
        defaults: resolved || {
          fdate: dayjs(),
          timeBeg: minutesToDayjs(dayjs(), startSlot * step),
          timeEnd: minutesToDayjs(dayjs(), (startSlot + 1) * step)
        }
      });
    },
    [zal?.idZal, startSlot, step, selectedCell]
  );

  const openEdit = useCallback(() => {
    if (!zal?.idZal || selectedId == null) return;
    setSessionDrawer({ isNew: false, idStr: selectedId, defaults: null });
  }, [zal?.idZal, selectedId]);

  const selectSession = useCallback(
    (idStr) => {
      setSelectedId(idStr);
      setSelectedCell(null);
      const session = sessionById.get(idStr);
      if (session?.cntPlan > 0) setFactSession(session);
    },
    [sessionById]
  );

  const openFact = useCallback(() => {
    if (selectedId == null) return;
    const session = sessionById.get(selectedId);
    if (session) setFactSession(session);
  }, [selectedId, sessionById]);

  const selectEmptyCell = useCallback((day, slot) => {
    setSelectedId(null);
    setSelectedCell({
      dayKey: day.format("YYYY-MM-DD"),
      minutes: slot.minutes,
      fdate: day
    });
  }, []);

  const onCopy = useCallback(async () => {
    if (!zal?.idZal || selectedId == null) return;
    setClipboardBusy(true);
    try {
      const [row, uslRows] = await Promise.all([
        fetchSprZalGrafSession(zal.idZal, selectedId),
        fetchSprZalGrafUsl(zal.idZal, selectedId)
      ]);
      await writeGrafSessionClipboard({
        cntMax: toNumber(pick(row, "cntMax", "CntMax")) ?? 1,
        idSotr: toNumber(pick(row, "idSotr", "IdSotr")) || 0,
        idSotrRun: toNumber(pick(row, "idSotrRun", "IdSotrRun")) || 0,
        timeBeg: formatHm(pick(row, "timeBeg", "TimeBeg")),
        timeEnd: formatHm(pick(row, "timeEnd", "TimeEnd")),
        usl: (Array.isArray(uslRows) ? uslRows : [])
          .map((u) => toNumber(pick(u, "idUsl", "IdUsl")))
          .filter((id) => id > 0)
      });
      message.success("Сеанс скопирован в буфер обмена");
    } catch (e) {
      message.error(e.message || "Не удалось скопировать");
    } finally {
      setClipboardBusy(false);
    }
  }, [zal?.idZal, selectedId]);

  const onPaste = useCallback(async () => {
    if (!zal?.idZal) return;
    if (!selectedCell) {
      message.warning("Выберите пустую ячейку для вставки");
      return;
    }
    setClipboardBusy(true);
    try {
      const data = await readGrafSessionClipboard();
      const day = selectedCell.fdate;
      const begMin = selectedCell.minutes;
      const srcBeg = parseHm(data.timeBeg, day);
      const srcEnd = parseHm(data.timeEnd, day);
      const duration = Math.max(step, srcEnd.diff(srcBeg, "minute") || step);
      const timeBeg = minutesToDayjs(day, begMin);
      const timeEnd = minutesToDayjs(day, begMin + duration);
      setSessionDrawer({
        isNew: true,
        idStr: null,
        defaults: {
          fdate: day,
          timeBeg,
          timeEnd,
          cntMax: data.cntMax,
          idSotr: data.idSotr,
          idSotrRun: data.idSotrRun,
          uslIds: data.usl
        }
      });
    } catch (e) {
      message.error(e.message || "Не удалось вставить");
    } finally {
      setClipboardBusy(false);
    }
  }, [zal?.idZal, selectedCell, step]);

  const onSessionSaved = useCallback(
    (row) => {
      const id = toNumber(pick(row, "idStr", "IdStr"));
      if (id != null) {
        setSelectedId(id);
        setSelectedCell(null);
        setItems((prev) => {
          const rest = (prev || []).filter((x) => toNumber(pick(x, "idStr", "IdStr")) !== id);
          return [...rest, row];
        });
      }
      load();
    },
    [load]
  );

  const onSessionDeleted = useCallback(() => {
    setSelectedId(null);
    setSelectedCell(null);
    load();
  }, [load]);

  return (
    <div className="spr-zal-graf">
      <div className="spr-zal-graf__title">График работы кабинета {zal?.name || ""}</div>
      <div className="spr-zal-graf__period">
        <span>Период для планирования</span>
        <RangePicker
          value={period}
          onChange={(v) => setPeriod(v || defaultPeriod())}
          format="DD.MM.YYYY"
          allowClear={false}
        />
        <Space size={4} style={{ flex: "none" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            title="Добавить"
            aria-label="Добавить"
            onClick={() => openAdd()}
          />
          <Button
            icon={<EditOutlined />}
            title="Изменить"
            aria-label="Изменить"
            disabled={selectedId == null}
            onClick={openEdit}
          />
          <Button disabled={selectedId == null} loading={clipboardBusy} onClick={onCopy}>
            Копировать
          </Button>
          <Button disabled={!selectedCell} loading={clipboardBusy} onClick={onPaste}>
            Вставить
          </Button>
          <Button disabled={selectedId == null} onClick={() => setCopyModalOpen(true)}>
            Копия
          </Button>
          <Button disabled={selectedId == null} onClick={openFact}>
            Факт
          </Button>
        </Space>
      </div>
      {error ? <Alert type="error" showIcon message={error} className="spr-zal-graf__error" /> : null}
      <div className="spr-zal-graf__body">
        <Spin spinning={loading}>
          {!slots.length ? (
            <div className="spr-zal-graf-day__empty">Не задан интервал работы кабинета</div>
          ) : (
            <div className="spr-zal-graf-day">
              <table className="spr-zal-graf-day__table">
                <thead>
                  <tr>
                    <th className="spr-zal-graf-day__corner" />
                    {days.map((d) => {
                      const weekend = d.day() === 0 || d.day() === 6;
                      return (
                        <th
                          key={d.format("YYYY-MM-DD")}
                          className={`spr-zal-graf-day__day${weekend ? " spr-zal-graf-day__day--weekend" : ""}`}
                        >
                          <div className="spr-zal-graf-day__day-label">
                            <span className="spr-zal-graf-day__day-num">{d.format("D")}</span>
                            <span className="spr-zal-graf-day__day-month">{d.format("MM")}</span>
                            <span className="spr-zal-graf-day__day-weekday">{WEEKDAYS[d.day()]}</span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {slots.map((slot, slotOffset) => (
                    <tr key={slot.minutes}>
                      <th
                        className={`spr-zal-graf-day__time${slot.isHour ? " spr-zal-graf-day__time--hour" : ""}`}
                      >
                        {slot.label}
                      </th>
                      {days.map((d) => {
                        const dayKey = d.format("YYYY-MM-DD");
                        if (coveredSlots.get(dayKey)?.has(slotOffset)) return null;

                        const weekend = d.day() === 0 || d.day() === 6;
                        const session = daySessions.get(dayKey)?.get(slotOffset);
                        if (session) {
                          const isSelected = selectedId === session.idStr;
                          return (
                            <td
                              key={dayKey}
                              rowSpan={session.span}
                              className={[
                                "spr-zal-graf-day__session",
                                session.noUsl ? "spr-zal-graf-day__session--no-usl" : "",
                                isSelected ? "spr-zal-graf-day__session--selected" : ""
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => selectSession(session.idStr)}
                              title={session.label}
                            >
                              <span className="spr-zal-graf-day__session-text">{session.label}</span>
                              {session.cntPlan > 0 ? (
                                <span
                                  className={`spr-zal-graf-group__badge spr-zal-graf-group__badge--${session.factTone}`}
                                  title={`Записано: ${session.cntPlan}`}
                                >
                                  {session.cntPlan}
                                </span>
                              ) : null}
                            </td>
                          );
                        }

                        const cellSelected =
                          selectedCell?.dayKey === dayKey && selectedCell?.minutes === slot.minutes;
                        return (
                          <td
                            key={dayKey}
                            className={[
                              "spr-zal-graf-day__cell",
                              weekend ? "spr-zal-graf-day__cell--weekend" : "",
                              cellSelected ? "spr-zal-graf-day__cell--selected" : ""
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => selectEmptyCell(d, slot)}
                            title="Выбрать ячейку"
                          />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Spin>
      </div>

      <SprZalGrafSessionDrawer
        open={Boolean(sessionDrawer)}
        idZal={zal?.idZal}
        zalName={zal?.name}
        idStr={sessionDrawer?.idStr}
        isNew={Boolean(sessionDrawer?.isNew)}
        defaults={sessionDrawer?.defaults}
        onClose={() => setSessionDrawer(null)}
        onSaved={onSessionSaved}
        onDeleted={onSessionDeleted}
      />

      <SprZalGrafCopyModal
        open={copyModalOpen}
        idZal={zal?.idZal}
        idStr={selectedId}
        zal={zal}
        onClose={() => setCopyModalOpen(false)}
        onDone={load}
      />

      <SprZalGrafFactModal
        open={Boolean(factSession)}
        idZal={zal?.idZal}
        idStr={factSession?.idStr}
        fdate={factSession?.fdate}
        timeLabel={factSession?.timeLabel}
        zalName={zal?.name}
        onClose={() => setFactSession(null)}
        onChanged={load}
      />
    </div>
  );
};

export default SprZalGraf;
